import { useCallback, useEffect, useState } from "react";
import ReduxFetchState from "redux-fetch-state";
import { put, takeEvery } from "redux-saga/effects";
import axios from "../../../../Utils/axiosConfig";
import { getCurrentLanguage } from "../../../../Utils/sagaHelpers";
import { useAppDispatch, useAppSelector } from "../../../../Hooks/useStore";
import { call } from "@redux-saga/core/effects";
import { AxiosError } from "axios";
import { alert } from "../../../../Utils/toast";

export type GetTextToSpeechLoadAction = {
  type: string;
  payload: {
    id?: number | string;
    text?: string;
    requestId: string;
  };
};

export type GetTextToSpeechData = {
  data: string | null;
  requestId: string | null;
};

export const GetTextToSpeech = new ReduxFetchState<
  GetTextToSpeechData | null,
  { id?: number | string; text?: string, requestId: string; },
  any
>("getTextToSpeech");

const bufferToBase64 = (buffer: ArrayBuffer) => {
  let binary = "";
  let bytes = new Uint8Array(buffer);

  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return window.btoa(binary);
};

export function* watchGetTextToSpeech(action: GetTextToSpeechLoadAction) {
  try {
    const lang: "fa" | "en" = yield getCurrentLanguage();

    const res: ArrayBuffer = yield call(() =>
      axios({
        url: `api/v1/ai/third-party/text-to-speech`,
        method: "POST",
        headers: { lang },
        data: {
          ...(action.payload.text !== undefined && {
            text: action.payload.text,
          }),
          ...(action.payload.id !== undefined && {
            session_id: action.payload.id,
          }),
        },
        responseType: "arraybuffer",
      }).then((r) => r.data),
    );

    const base64Audio = bufferToBase64(res);

    yield put(GetTextToSpeech.actions.loadSuccess({
       data: base64Audio,
      requestId: action.payload.requestId,
     }));
  } catch (e: unknown) {
    let errorData: any = null;

    if (e instanceof AxiosError) {
      if (e.response?.data instanceof ArrayBuffer) {
        const text = new TextDecoder().decode(e.response.data);
        try {
          errorData = JSON.parse(text);
        } catch {
          errorData = { message: text };
        }
      } else {
        errorData = e.response?.data;
      }
    } else if (e instanceof Error) {
      errorData = { message: e.message };
    }

    const rawHtml = errorData.error || "";
    const plainText = rawHtml.replace(/<[^>]*>/g, "");
    alert({
      message: plainText,
      type: "error",
    });
    yield put(GetTextToSpeech.actions.loadFailure(errorData));
  }
}

export function* getTextToSpeechSagas() {
  yield takeEvery(GetTextToSpeech.actionTypes.load, watchGetTextToSpeech);
}

export function useGetTextToSpeech() {
  const { loading, data, ...getTextToSpeechState } = useAppSelector(
    (state) => state.getTextToSpeech,
  );

  const dispatch = useAppDispatch();

  const [loadingId, setLoadingId] = useState<number | string | null>(null);
const dispatchGetTextToSpeech = useCallback(
  (data: {
    id?: number | string;
    text?: string;
    types?: string | null;
    requestId: string;
  }) => {
    dispatch(GetTextToSpeech.actions.loadSuccess({ data: null,requestId: data.requestId }));
    setLoadingId(data.requestId);
    dispatch(GetTextToSpeech.actions.load(data));
  },
  [dispatch],
);

  useEffect(() => {
    if (!loading) {
      setLoadingId(null);
    }
  }, [loading]);

  return {
    ...getTextToSpeechState,
    dispatchGetTextToSpeech,
    getTextToSpeechLoadingId: loadingId,
    getTextToSpeechData: data,
  };
}

export const {
  actionTypes: getTextToSpeechActionsTypes,
  actions: getTextToSpeechActions,
  reducer: getTextToSpeechReducer,
} = GetTextToSpeech;
